import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '../../pages/app/protected/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { quotas } from '../../pages/app/protected/services/QuotasConfig'; 

export default function ProfitLossReport() {
  const { user ,companyId} = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [moneyInTotal, setMoneyInTotal] = useState(0);
  const [moneyOutTotal, setMoneyOutTotal] = useState(0);
  const [companyName, setCompanyName] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [plan, setPlan] = useState('');
  const [loading, setLoading] = useState(true);
  const toNumber = (val) => {
    const num = Number(val);
    return Number.isFinite(num) ? num : 0;
  };
  const parseTimestamp = (ts) => {
    if (!ts) return null;
    if (typeof ts.toDate === 'function') return ts.toDate();
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? null : d;
  };

const loadPlan = async () => {
  const docRef = doc(db, 'companies', companyId);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    setPlan(docSnap.data()?.subscription?.plan || 'Free');
  }
};

  useEffect(() => {
    loadPlan();
    // Set today's date as default for both from and to date
    const today = new Date().toISOString().split('T')[0];
    setFromDate(today);
    setToDate(today);
  }, []);

  const loadOrders = async () => {
    if (!user || !companyId) return;
    try {
      const q = query(
        collection(db, 'companies', companyId, 'orders'),
        where('paymentStatus', '!=', 'Void'),
        orderBy('timestamp', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const orders = querySnapshot.docs.map(doc => doc.data());
      
      // Filter by date
      const filteredOrders = orders.filter(order => {
        const orderDate = parseTimestamp(order.timestamp);
        if (!orderDate) return false;
        const from = fromDate ? new Date(fromDate) : new Date(0);
        const to = toDate ? new Date(toDate) : new Date();
        to.setHours(23, 59, 59, 999);
        return orderDate >= from && orderDate <= to;
      });

      setOrders(filteredOrders);
    } catch (error) {
      console.error('Error loading orders:', error);
    }
  };

  const loadInventory = async () => {
    if (!user || !companyId) return;
    const snap = await getDocs(collection(db, 'companies', companyId, 'inventory', 'data', 'products'));
    const list = snap.docs.map(doc => doc.data());
    setProducts(list);
  };

  const loadCompanyName = async () => {
    if (!user || !companyId) return;
    const snap = await getDoc(doc(db, 'companies', companyId, 'companyProfile', 'info'));
    if (snap.exists()) setCompanyName(snap.data().companyName || 'Company');
  };

  const loadAccountingEntries = async () => {
    if (!user || !companyId) return;
    const start = fromDate ? new Date(fromDate) : new Date(0);
    const end = toDate ? new Date(toDate) : new Date();
    end.setHours(23, 59, 59, 999);

    const [moneyInSnap, moneyOutSnap] = await Promise.all([
      getDocs(collection(db, 'companies', companyId, 'moneyIn')),
      getDocs(collection(db, 'companies', companyId, 'moneyOut')),
    ]);

    const sumWithinRange = (docs) =>
      docs
        .map(d => d.data())
        .filter(entry => {
          const date = parseTimestamp(entry.date);
          if (!date) return false;
          return date >= start && date <= end;
        })
        .reduce((acc, entry) => acc + toNumber(entry.amount || 0), 0);

    setMoneyInTotal(sumWithinRange(moneyInSnap.docs));
    setMoneyOutTotal(sumWithinRange(moneyOutSnap.docs));
  };

  // Calculate totals
  let revenue = 0;
  let totalDiscount = 0;
  let sst6Amount = 0;
  let sst8Amount = 0;
  let serviceTax10Amount = 0;
  let rounding = 0;
  let inventoryCost = 0;
  let totalBundleLoss = 0;

  // Build maps for quick lookup
  const productMapById = products.reduce((acc, p) => {
    if (p.id) acc[p.id] = p;
    return acc;
  }, {});
  const productMapBySku = products.reduce((acc, p) => {
    if (p.sku) acc[p.sku] = p;
    return acc;
  }, {});
  const productMapByName = products.reduce((acc, p) => {
    if (p.name) acc[p.name] = p;
    return acc;
  }, {});

  const resolveProduct = (item) => {
    if (item.id && productMapById[item.id]) return productMapById[item.id];
    if (item.sku && productMapBySku[item.sku]) return productMapBySku[item.sku];
    if (item.name && productMapByName[item.name]) return productMapByName[item.name];
    return null;
  };

  const getPurchaseCost = (prod, variantId) => {
    if (!prod) return 0;
    if (variantId && Array.isArray(prod.variants)) {
      const v = prod.variants.find(
        (opt) => opt.id === variantId || opt.variantId === variantId
      );
      if (v) {
        const variantPurchase = toNumber(v.purchase ?? v.cost ?? prod.purchase);
        return variantPurchase;
      }
    }
    return toNumber(prod.purchase);
  };

  let cogs = 0;

  orders.forEach(order => {
    revenue += toNumber(order.total);
    totalDiscount += toNumber(order.discountAmount);
    sst6Amount += toNumber(order.sst6Amount);
    sst8Amount += toNumber(order.sst8Amount);
    serviceTax10Amount += toNumber(order.serviceTax10Amount);
    rounding += toNumber(order.rounding);
    
    // For COGS calculation
    (order.items || []).forEach(item => {
      const orderQty = toNumber(item.qty || item.quantity || 0);
      totalBundleLoss += toNumber(item.bundleLoss);

      // Bundles: each child counts toward COGS
      if (item.items && Array.isArray(item.items)) {
        const parentQty = orderQty || 1;
        item.items.forEach(sub => {
          const subProd = resolveProduct(sub);
          const subVariantId = sub.variantId || sub.variant_id;
          const costPerUnit = getPurchaseCost(subProd, subVariantId);
          const subQty = parentQty * toNumber(sub.quantity || 1);
          cogs += costPerUnit * subQty;
        });
        return;
      }

      // Regular product
      const prod = resolveProduct(item);
      const costPerUnit = getPurchaseCost(prod, item.variantId || item.variant_id);
      cogs += costPerUnit * orderQty;
    });
  });

  const totalTax = sst6Amount + sst8Amount + serviceTax10Amount;

  // Inventory asset: use variant-specific qty when present
  products.forEach(product => {
    const basePurchase = toNumber(product.purchase);
    if (Array.isArray(product.variants) && product.variants.length > 0) {
      product.variants.forEach(v => {
        const vPurchase = toNumber(v.purchase ?? v.cost ?? basePurchase);
        inventoryCost += vPurchase * toNumber(v.qty);
      });
    } else {
      inventoryCost += basePurchase * toNumber(product.qty);
    }
  });

  const totalRevenue = revenue + moneyInTotal;
  const grossProfit = totalRevenue - cogs;
  const netProfit = grossProfit - totalDiscount - totalBundleLoss - totalTax - moneyOutTotal + rounding;

  const exportToPDF = () => {
    if (!quotas[plan]?.exportPDF) {
      alert('Export to PDF is only available for Plus and Premium plans.');
      return;
    }

    const docPDF = new jsPDF();
    const title = `Profit & Loss Report (${formatDate(fromDate)} - ${formatDate(toDate)})`;
    
    docPDF.setFontSize(12);
    docPDF.text(companyName, 105, 15, { align: 'center' });
    docPDF.setFontSize(10);
    docPDF.text(title, 105, 22, { align: 'center' });

    const data = [
      ['Revenue (Sales)', `RM ${revenue.toFixed(2)}`],
      ['Money In (Accounting)', `RM ${moneyInTotal.toFixed(2)}`],
      ['Inventory Asset', `RM ${inventoryCost.toFixed(2)}`],
      ['Cost of Goods Sold', `RM ${cogs.toFixed(2)}`],
      ['Gross Profit', `RM ${grossProfit.toFixed(2)}`],
      ['Money Out (Adjustments)', `-RM ${moneyOutTotal.toFixed(2)}`],
      ['Discounts Given', `-RM ${totalDiscount.toFixed(2)}`],
      ['Bundle Discounts', `-RM ${totalBundleLoss.toFixed(2)}`],
      ['SST 6%', `RM ${sst6Amount.toFixed(2)}`],
      ['SST 8%', `RM ${sst8Amount.toFixed(2)}`],
      ['Service Tax 10%', `RM ${serviceTax10Amount.toFixed(2)}`],
      ['Rounding', `RM ${rounding.toFixed(2)}`],
      ['', ''],
      ['Net Profit', `RM ${netProfit.toFixed(2)}`],
    ];

    autoTable(docPDF, {
      head: [['Description', 'Amount (RM)']],
      body: data,
      startY: 30,
      styles: { fontSize: 9 }
    });

    const pageHeight = docPDF.internal.pageSize.height;
    docPDF.setFontSize(9);
    docPDF.text('Powered by CashMate', 105, pageHeight - 10, { align: 'center' });

    const file = `profit-loss_${formatDate(fromDate)}_to_${formatDate(toDate)}.pdf`.replace(/\//g, '-');
    docPDF.save(file);
  };

  const formatDate = (date) => {
    const d = new Date(date);
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
  };
  const formatTime = (date) => {
    const d = new Date(date);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

    useEffect(() => {
      const loadAll = async () => {
        setLoading(true);
        await Promise.all([loadOrders(), loadInventory(), loadCompanyName(), loadAccountingEntries()]);
        setLoading(false);
      };
      loadAll();
    }, [user, fromDate, toDate]);
  
    if (loading) {
      return (
        <div className="w-full flex flex-col justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-purple-500 mb-4"></div>
          <span className="text-white">Loading information...</span>
        </div>
      );
    }

  return (
    <div className="p-6 text-white">
      <div className="flex items-center mb-4 gap-2">
        <button onClick={() => navigate(-1)} className="text-sm flex items-center gap-1 text-gray-300 hover:text-white">
          <span className="text-lg">←</span> Back
        </button>
      </div>

      <h2 className="text-2xl font-bold mb-4">Profit & Loss Report</h2>

      <div className="flex gap-4 mb-6 flex-wrap">
        <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="bg-[#1f1f1f] px-3 py-2 rounded" />
          <span className="text-gray-400">to</span>
        <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="bg-[#1f1f1f] px-3 py-2 rounded" />
        <button onClick={exportToPDF} className="bg-purple-600 px-4 py-2 rounded">Export to PDF</button>
      </div>

      <div className="space-y-6 bg-[#1a1a1a] rounded-lg p-6">
        <div>
          <h3 className="text-lg font-bold">ASSETS</h3>
            <div className="flex justify-between">
              <span>Total Revenue</span>
              <span>RM {totalRevenue.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm text-gray-400">
              <span className="ml-2">• Sales Revenue</span>
              <span>RM {revenue.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm text-gray-400">
              <span className="ml-2">• Money In (Accounting)</span>
              <span>RM {moneyInTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
            <span>Inventory</span>
            <span>RM {inventoryCost.toFixed(2)}</span>
            </div>
        </div>

        <div>
          <h3 className="text-lg font-bold">COST OF GOODS SOLD</h3>
          <div className="flex justify-between">
            <span>COGS</span>
            <span>- RM {cogs.toFixed(2)}</span>
            </div>
          </div>

        <div className="flex justify-between font-semibold">
          <span>GROSS PROFIT</span>
          <span>RM {grossProfit.toFixed(2)}</span>
        </div>

        <div>
          <h3 className="text-lg font-bold">ADJUSTMENTS</h3>
            <div className="flex justify-between">
              <span>Discounts Given</span>
              <span>- RM {totalDiscount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>Bundle Discounts</span>
              <span>- RM {totalBundleLoss.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>Money Out (Accounting)</span>
              <span>- RM {moneyOutTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>Tax</span>
              <span>- RM {totalTax.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
            <span>Rounding Adjustment</span>
              <span>RM {rounding.toFixed(2)}</span>
            </div>
          </div>

        <div className={`text-xl font-bold flex justify-between ${netProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
          <span>{netProfit >= 0 ? 'PROFIT' : 'LOSS'}</span>
          <span>{netProfit >= 0 ? 'RM' : '- RM'} {Math.abs(netProfit).toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
}
